import { Trans, useLingui } from "@lingui/react/macro";
import type {
  ConnectedBackupOption,
  ModelBackupChoice,
  ModelCatalogEntry,
} from "@rakazo/contracts";
import { backupChoiceKey, MAX_MODEL_BACKUPS, moveBackupChoice } from "@rakazo/contracts";
import type { ChatModelChoice } from "@rakazo/core";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@rakazo/ui-web";
import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { useState } from "react";
import { ModelChoiceCommand } from "../components/ModelChoiceCommand";
import { providerDisplayName } from "./model-settings/provider-name";

/** Connected models that are not in the list yet and still fit under the limit. */
export function addableBackupOptions(
  backups: ModelBackupChoice[],
  options: ConnectedBackupOption[],
): ConnectedBackupOption[] {
  if (backups.length >= MAX_MODEL_BACKUPS) return [];
  const used = new Set(backups.map(backupChoiceKey));
  return options.filter((option) => !used.has(backupChoiceKey(option)));
}

/**
 * An ordered backup list that can be reordered, trimmed and added to. It holds no state of its
 * own: the owner decides when and where the new list is saved.
 */
export function BackupModelList({
  backups,
  options,
  catalog,
  editable,
  allowAdd = true,
  onChange,
}: {
  backups: ModelBackupChoice[];
  /** Models the person has connected, from `connectedBackupOptions`. */
  options: ConnectedBackupOption[];
  catalog: ModelCatalogEntry[];
  editable: boolean;
  /** False hides the add menu, for a list that has not loaded. */
  allowAdd?: boolean;
  onChange: (next: ModelBackupChoice[]) => void;
}) {
  const { t } = useLingui();
  const [adding, setAdding] = useState(false);
  const optionByKey = new Map(options.map((option) => [backupChoiceKey(option), option]));
  const addable = addableBackupOptions(backups, options);
  const addChoices: ChatModelChoice[] = addable.map((option) => {
    const providerName = providerDisplayName(catalog, option.provider);
    return {
      key: backupChoiceKey(option),
      label: `${providerName} · ${option.label}`,
      providerLabel: providerName,
      modelLabel: option.label,
    };
  });

  const rowLabel = (choice: ModelBackupChoice) => {
    const option = optionByKey.get(backupChoiceKey(choice));
    const entry = catalog.find(
      (candidate) => candidate.provider === choice.provider && candidate.id === choice.modelId,
    );
    const providerName = providerDisplayName(catalog, choice.provider);
    const label = option?.label ?? entry?.label ?? choice.modelId;
    return { full: `${providerName} · ${label}`, label, providerName, available: Boolean(option) };
  };

  function add(option: ConnectedBackupOption) {
    if (backups.some((choice) => backupChoiceKey(choice) === backupChoiceKey(option))) return;
    onChange([...backups, { provider: option.provider, modelId: option.modelId }]);
  }

  return (
    <>
      {backups.length > 0 ? (
        <ol className="mt-3 divide-y divide-border rounded-xl border border-border">
          {backups.map((choice, index) => {
            const { full, label, providerName, available } = rowLabel(choice);
            return (
              <li
                key={backupChoiceKey(choice)}
                className="flex items-center gap-3 py-2 ps-3 pe-2"
                data-testid={`model-backup-${index + 1}`}
              >
                <span className="w-4 shrink-0 text-center text-[13px] tabular-nums text-muted-foreground">
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-[15px] text-foreground">{label}</span>
                  <span className="block break-words text-[13px] leading-[1.5] text-muted-foreground">
                    {providerName}
                  </span>
                  {!available ? (
                    <span className="block text-[13px] leading-[1.5] text-muted-foreground">
                      <Trans>Not connected</Trans>
                    </span>
                  ) : null}
                </span>
                <div className="flex shrink-0 items-center text-muted-foreground">
                  {backups.length > 1 ? (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t`Move ${full} up`}
                        disabled={index === 0 || !editable}
                        onClick={() => onChange(moveBackupChoice(backups, index, -1))}
                      >
                        <ArrowUp />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t`Move ${full} down`}
                        disabled={index === backups.length - 1 || !editable}
                        onClick={() => onChange(moveBackupChoice(backups, index, 1))}
                      >
                        <ArrowDown />
                      </Button>
                    </>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t`Remove ${full}`}
                    disabled={!editable}
                    onClick={() => onChange(backups.filter((_, item) => item !== index))}
                  >
                    <X />
                  </Button>
                </div>
              </li>
            );
          })}
        </ol>
      ) : null}
      {allowAdd && addable.length > 0 ? (
        <Popover open={adding} onOpenChange={setAdding}>
          <PopoverTrigger
            disabled={!editable}
            render={<Button type="button" variant="outline" className="mt-3" />}
          >
            <Plus data-icon="inline-start" />
            <Trans>Add backup model</Trans>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="bottom"
            collisionAvoidance={{ side: "flip", align: "shift", fallbackAxisSide: "none" }}
            className="w-80 max-w-(--available-width) max-h-(--available-height) gap-0 overflow-hidden bg-popover p-0"
          >
            <ModelChoiceCommand
              choices={addChoices}
              onSelect={(choice) => {
                const option = optionByKey.get(choice.key);
                if (option) add(option);
                setAdding(false);
              }}
            />
          </PopoverContent>
        </Popover>
      ) : null}
    </>
  );
}
